import * as React from "react";
import { useMemo } from "react";
import { Chips } from "~/sharedComponents/chips/Chips";
import { OptionsWithColor } from "~/sharedComponents/colorCoding/useWithColorCoding";
import { Highlighter } from "~/sharedComponents/highlighter";
import { ItemKey } from "~/sharedComponents/list";
import { ListProps } from "~/sharedComponents/list/List";

type ChipsProps<TItem> = React.ComponentProps<typeof Chips> & { item: TItem };
export type SelectedItemsProps<TItem> = Required<Pick<ListProps<TItem>, "getItemKey" | "getItemLabel">> & {
    selectedItems: TItem[];
    onChange?: F1<TItem>;
    showTitle?: boolean;
    textForHighlight?: string;
    ChipsComponent?: React.ComponentType<ChipsProps<TItem>>;
};

export type GetSelectedItemsProps<TItem> = Required<Pick<ListProps<TItem>, "getItemKey">> & {
    value: ItemKey[];
    options: TItem[];
};

export const getSelectedItems = <TItem,>({ value, options, getItemKey }: GetSelectedItemsProps<TItem>) =>
    value?.length && options?.length
        ? value.map(id => options.find(option => getItemKey(option) === id)).filter(x => !!x)
        : [];

export const useSelectedItems = <TItem,>({ value, options, getItemKey }: GetSelectedItemsProps<TItem>) => {
    return useMemo(() => getSelectedItems({ value, options, getItemKey }), [value, options]);
};

export function SelectedItems<TItem extends OptionsWithColor>({
    selectedItems,
    onChange,
    showTitle = true,
    getItemKey,
    getItemLabel,
    textForHighlight,
    ChipsComponent = Chips,
}: SelectedItemsProps<TItem>) {
    const onChangeAction = (x: TItem) => () => {
        onChange(x);
    };

    return (
        <>
            {selectedItems.map(x => {
                const label = getItemLabel(x);
                return (
                    <ChipsComponent
                        key={getItemKey(x)}
                        minWidth={48}
                        onChange={onChange && onChangeAction(x)}
                        backgroundColor={x.color}
                        data-testid={label}
                        item={x}
                    >
                        <Highlighter textForHighlight={textForHighlight} text={label} truncate={showTitle} />
                    </ChipsComponent>
                );
            })}
        </>
    );
}
